import { createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from 'store';
import { EventOrderStatus } from 'types';
import { eventOrdersApi } from 'store/api/event-orders';

export interface EventOrderTrackingEvent {
  dateShipped: string;
  trackingUrl: string;
  eventOrderId: number;
  trackingNumber: number;
  status: EventOrderStatus;
}

/**
 * Thunk for updating trackingNumber, date shipped and status in the eventOrders cache.
 *
 * Updates:
 * - updates order details
 * - updates order list
 */
export const updateEventOrderInCache = createAsyncThunk<
  void,
  EventOrderTrackingEvent,
  { state: RootState }
>(
  'eventOrders/update-tracking-cache',
  async (event, { dispatch, getState }) => {
    const { status, dateShipped, trackingUrl, eventOrderId, trackingNumber } =
      event;
    const state = getState();

    const detailsEndpointName = 'details';
    const listEndpointName = 'list';

    const detailsParams = Object.values(state.eventOrdersApi.queries)
      .filter(
        (queryDetails) =>
          queryDetails?.endpointName === detailsEndpointName &&
          String(queryDetails.originalArgs) === String(eventOrderId)
      )
      .map((queryDetails) => queryDetails!.originalArgs as unknown as string);

    const listParams = Object.values(state.eventOrdersApi.queries)
      .filter(
        (queryDetails) =>
          queryDetails?.endpointName === listEndpointName &&
          queryDetails.originalArgs
      )
      .map((queryDetails) => queryDetails!.originalArgs);

    detailsParams.forEach((params) => {
      dispatch(
        eventOrdersApi.util.updateQueryData(
          detailsEndpointName,
          params,
          (draft: any) => {
            if (draft?.id === eventOrderId) {
              draft.trackingUrl = trackingUrl;
              draft.trackingNumber = trackingNumber;
              if (status) {
                draft.status = status;
              }
              if (dateShipped) {
                draft.dateShipped = dateShipped;
              }
            }
          }
        )
      );
    });

    listParams.forEach((params) => {
      dispatch(
        eventOrdersApi.util.updateQueryData(
          listEndpointName,
          params as any,
          (draft: any) => {
            if (Array.isArray(draft)) {
              draft.forEach((order) => {
                if (order.id === eventOrderId) {
                  order.trackingUrl = trackingUrl;
                  order.trackingNumber = trackingNumber;
                  if (status) {
                    order.status = status;
                  }
                  if (dateShipped) {
                    order.dateShipped = dateShipped;
                  }
                }
              });
            }
          }
        )
      );
    });
  }
);
